import { CommonModule } from '@angular/common';
import { Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
  ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';
import { getAvatarBgColor } from '../../../helpers/avatar.helper';
import { InitialsService } from '../../../helpers/initial.service';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { AuthService } from '../../../api/services/auth.service';
import { NoDataViewComponent } from '../no-data-view/no-data-view.component';
import { CommonService } from '../../../api/services/common.service';
import { GraphServiceService } from '../../../api/services/graph-service.service';
import { ThemeColorService } from '../../../helpers/theme-color.service';

@Component({
    selector: 'app-card-type-two',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ModalRendererComponent,
        NoDataViewComponent,
    ],
    templateUrl: './card-type-two.component.html',
    styleUrl: './card-type-two.component.css'
})
export class CardTypeTwoComponent {
  @Input() formData: any;
  @Input() viewMode: any;
  @Input() filterOptions: any;
  @Input() tasksLimit: any;
  @Input() isLoading: any;
  @Input() taskAssigned: any;
  @Input() selectAllAcrossPages: any;
  @Output() viewMoreClicked = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<{ formId: string; options?: any }>();
  @Output() loadData = new EventEmitter<{ formId: string; options?: any }>();
  @Output() selectionChanged = new EventEmitter<string[]>();
  @Output() closeDropdown = new EventEmitter<any>();
  @Input() resetSelectionTrigger: boolean = false;
  @ViewChild(ModalRendererComponent) modalRendererRef!: ModalRendererComponent;
  accessToken: any;
  nodeId: any;
  modalTitle: any;
  showAssign: boolean = false;
  activeModalContext: any;
  riskStatus: any;
  actionableSearch: any;
  domain: any;
  taskSearch: any;
  priority: any;
  taskStatus: any;
  @Input() selectedIds: any[] = [];
  selectedItems: any[] = [];
  taskPriority: any;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private initialsService: InitialsService,
    private auth: AuthService,
    private commonService: CommonService,
    private graphService: GraphServiceService,
    public themeColorService: ThemeColorService,
   private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.accessToken = this.auth.token;
    this.getParams();
  }

  ngOnChanges(changes: SimpleChanges) {
    // 🔒 DO NOT write to sessionStorage here

    if (changes['taskAssigned']) {
      this.taskAssigned = changes['taskAssigned'].currentValue;
    }

    if (changes['taskSearch']) {
      this.taskSearch = changes['taskSearch'].currentValue;
    }

    if (changes['status']) {
      this.taskStatus = changes['status'].currentValue;
    }

    if (changes['priority']) {
      this.priority = changes['priority'].currentValue;
    }

    if (changes['risk']) {
      this.riskStatus = changes['risk'].currentValue;
    }

    if (changes['domainSubdomainMappings']) {
      this.domain = changes['domainSubdomainMappings'].currentValue;
    }

    if (changes['tasksLimit']) {
      this.tasksLimit = changes['tasksLimit'].currentValue;
    }
  }

  /** Get params from the url */
  getParams(): void {
    const STORAGE_KEY = 'tasksView';

    const storedView = sessionStorage.getItem(STORAGE_KEY);
    if (!storedView) return;

    const storedState = JSON.parse(storedView);

    this.taskAssigned = storedState.taskAssigned ?? this.taskAssigned ?? '';
    this.taskSearch = storedState.taskSearch ?? '';
    this.taskStatus = storedState.status ?? '';
    this.taskPriority = storedState.priority ?? '';
    this.actionableSearch = storedState.actionableSearch ?? '';
    this.riskStatus = storedState.risk ?? '';
    this.domain = storedState.domainSubdomainMappings ?? '';
    this.tasksLimit = storedState.tasksLimit ?? 10;
  }

  /** Navigate to task view with the current taskId */
  navigateToTaskView(item: any) {
    // Navigate with PATH ONLY
    this.router.navigate(['/eciso/tasks', item.taskId]);
  }

  /** Emit the viewMore function from the parent component */
  viewMore() {
    this.viewMoreClicked.emit();
  }

  /** Close on modal backdrop click */
  closeOnBackdropClick(event: MouseEvent) {
    const modal = document.getElementById('assign_modal');
    if (event.target === modal) {
      this.closeModal();
    }
  }

  closeModal() {
    const modal = document.getElementById('assign_modal');
    if (modal) modal.classList.remove('show');
    this.modalRendererRef?.resetSearchInputs();
    this.activeModalContext = null;
  }

  /** Open Assign user modal with the formID */
  assignUsers(item: any) {
    document.dispatchEvent(new CustomEvent('closeAllOverlays'));
    const fieldDeps = [
      {
        trigger: 'onClick',
        condition: [
          {
            conditions: [
              {
                type: 'modal',
                targetType: 'form',
                targetValue: FORM_ID.TASK_ASSIGN,
              },
            ],
          },
        ],
      },
    ];
    const onClickDep = fieldDeps.find((dep: any) => dep.trigger === 'onClick');

    if (onClickDep && Array.isArray(onClickDep.condition)) {
      for (const condBlock of onClickDep.condition) {
        const modalCond = condBlock.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'form' && c.targetValue,
        );

        if (modalCond) {
          const modalContext = {
            nodeId: modalCond.targetValue,
            taskId: item._id,
            assigned: item.assigned,
            tasksCount: this.selectedIds.length,
          };
          this.activeModalContext = modalContext;
          const modal = document.getElementById('assign_modal');
          if (modal) modal.classList.add('show');
          return;
        }
      }
    }
  }

  /** Function to set the modal title */
  setModalTitle(title: string) {
    this.modalTitle = title;
  }

  /** Show color for the user department wise */
  getBgColorByDepartment(name: string): string {
    return getAvatarBgColor(name);
  }

  /** Get initials by the assigned user email ID */
  getInitials(name: string): string {
    return this.initialsService.getInitials(name);
  }

  /** On save modal close the modal and emit loadData function which is in the parent component */
  onModalFormSaved(event: any): void {
    if (!event?.success) {
      return;
    }

    const STORAGE_KEY = 'tasksView';

    /* -------------------- Close modal -------------------- */

    const modal = document.getElementById('assign_modal');
    if (modal) {
      modal.classList.remove('show');
    }

    this.activeModalContext = null;

    /* -------------------- Get values from sessionStorage -------------------- */

    const savedState = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

    /* -------------------- Reload data -------------------- */

    const formId = FORM_ID.TASK_LIST;
    const options: { [key: string]: any } = {};

    if (savedState.taskSearch) options['query'] = savedState.taskSearch;
    if (savedState.taskStatus) options['status'] = savedState.taskStatus;
    if (savedState.taskPriority) options['priority'] = savedState.taskPriority;
    if (savedState.riskStatus) options['risk'] = savedState.riskStatus;
    if (savedState.domainSubdomainMappings)
      options['domainSubdomainMappings'] = savedState.domainSubdomainMappings;
    if (savedState.assignedUser) options['assigned'] = savedState.assignedUser;
    if (savedState.tasksLimit) options['limit'] = savedState.tasksLimit;

    options['sort'] = '_id';

    if (savedState.taskAssigned) {
      options['assigned'] = savedState.taskAssigned;
    }

    this.loadData.emit({ formId, options });
    this.graphService.triggerGraphRefresh();
  }
  getPriority(priority: string): string {
    return priority && priority.trim() !== '' ? priority : 'Do Later';
  }

  getBadgeClass(type: string, value: string): string {
    if (!value && type === 'risk') {
      return 'badge-low dark:badge-low';
    }
    return this.themeColorService.getBadgeClass(value) || '';
  }

  // onEnterPressed(event: Event) {
  //   event.preventDefault();
  //   event.stopPropagation();

  //   const keyboardEvent = event as KeyboardEvent;

  //   if (this.modalRendererRef?.onSave) {
  //     this.modalRendererRef.onSave({ action: 'save' });
  //   }
  // }

  onCardClick(event: MouseEvent, item: any) {
    const selectedText = window.getSelection()?.toString();
    if (selectedText && selectedText.trim().length > 0) {
      event.stopPropagation();
      return;
    }
    this.navigateToTaskView(item);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeModal();
    this.closeModals();
    this.closeBulkModal();
    this.closeSuccessModal();
  }

  /*** Single row selection with assign user */
  onRowSelect(item: any, event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      const exists = this.selectedIds.some((x: any) => x._id === item._id);

      if (!exists) {
        this.selectedIds = [...this.selectedIds, item]; // replace array
      }
    } else {
      this.selectedIds = this.selectedIds.filter(
        (x: any) => x._id !== item._id,
      );
    }
    this.closeDropdown.emit();
    this.selectionChanged.emit(this.selectedIds);
  }

  isSelected(item: any): boolean {
    return this.selectedIds.some((selected: any) => selected._id === item._id);
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const currentRecords = this.formData?.records || [];

    if (checked) {
      // Add only records not already selected
      const newSelections = currentRecords.filter(
        (record: any) =>
          !this.selectedIds.some((sel) => sel._id === record._id),
      );

      this.selectedIds = [...this.selectedIds, ...newSelections];
    } else {
      this.selectedIds = [];
      this.selectAllAcrossPages = false;
    }
  }

  selectAllTasks(): void {
    this.selectAllAcrossPages = true;

    const formId = FORM_ID.TASK_LIST;

    const storedView = sessionStorage.getItem('tasksView');
    const storedState = storedView ? JSON.parse(storedView) : {};

    const options = {
      query: '',
      page: 1,
      limit: this.formData?.totalCount || 10,
      sort: '_id',
      ...(storedState.taskSearch && { query: storedState.taskSearch }),
      ...(storedState.taskStatus && { status: storedState.taskStatus }),
      ...(storedState.taskPriority && { priority: storedState.taskPriority }),
      ...(storedState.riskStatus && { risk: storedState.riskStatus }),
      ...(storedState.assignedUser && { assigned: storedState.assignedUser }),
      ...(storedState.domainSubdomainMappings && {
        domainSubdomainMappings: storedState.domainSubdomainMappings,
      }),
    };

    // 🔥 Call API without touching UI data
    this.commonService.loadListWithOptions(formId, options).subscribe({
      next: (data) => {
        const allRecords = data?.data?.records || [];
        // store all records in selection
        this.selectedIds = [...allRecords];
       this.cdr.detectChanges();},
      error: () => {
        this.selectedIds = [];
       this.cdr.detectChanges();},
    });
  }

  onSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    const currentRecords = this.formData?.records || [];

    if (checked) {
      this.selectedIds = [...currentRecords];
      this.selectAllAcrossPages = false;
    } else {
      this.selectedIds = [];
      this.selectAllAcrossPages = false;
    }
  }

  isAllSelected(): boolean {
    const currentRecords = this.formData?.records || [];

    return (
      currentRecords.length > 0 &&
      currentRecords.every((record: any) =>
        this.selectedIds.some((selected: any) => selected._id === record._id),
      )
    );
  }

  isIndeterminate(): boolean {
    const currentRecords = this.formData?.records || [];

    const selectedCount = currentRecords.filter((record: any) =>
      this.selectedIds.some((selected: any) => selected._id === record._id),
    ).length;

    return selectedCount > 0 && selectedCount < currentRecords.length;
  }

  /** Open Assign user modal with the formID */
  bulkUpdate(item: any) {
    this.activeModalContext = null;
    document.dispatchEvent(new CustomEvent('closeAllOverlays'));
    const fieldDeps = [
      {
        trigger: 'onClick',
        condition: [
          {
            conditions: [
              {
                type: 'modal',
                targetType: 'form',
                targetValue: FORM_ID.TASK_BULK_ASSIGN,
              },
            ],
          },
        ],
      },
    ];
    const onClickDep = fieldDeps.find((dep: any) => dep.trigger === 'onClick');

    if (onClickDep && Array.isArray(onClickDep.condition)) {
      for (const condBlock of onClickDep.condition) {
        const modalCond = condBlock.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'form' && c.targetValue,
        );

        if (modalCond) {
          const tasksCount = this.selectedIds.length;
          const labelText =
            tasksCount == 1
              ? '1 task selected for bulk update'
              : `${tasksCount} tasks selected for bulk update`;
          const modalContext = {
            nodeId: modalCond.targetValue,
            selectedTasks: item,
            assigned: item.assigned,
            tasksCount: this.selectedIds.length,
            bulkUpdate: true,
            formID: FORM_ID.TASK_BULK_ASSIGN,
            labelText: labelText,
          };
          this.activeModalContext = modalContext;
          setTimeout(() => {
            const modal = document.getElementById('bulk_assign_modal');
            if (modal) modal.classList.add('show');

            return;
          }, 100);
        }
      }
    }
  }

  openReassigneModal(event: any) {
    document.dispatchEvent(new CustomEvent('closeAllOverlays'));

    const { tasksCount, currentAssignee, oldAssignee } = event;

    const fieldDeps = [
      {
        trigger: 'onClick',
        condition: [
          {
            conditions: [
              {
                type: 'modal',
                targetType: 'form',
                targetValue: FORM_ID.TASK_REASSIGN,
              },
            ],
          },
        ],
      },
    ];

    const onClickDep = fieldDeps.find((dep: any) => dep.trigger === 'onClick');

    if (onClickDep && Array.isArray(onClickDep.condition)) {
      for (const condBlock of onClickDep.condition) {
        const modalCond = condBlock.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'form' && c.targetValue,
        );

        if (modalCond) {
          // 🔥 Normalize unassignedTasks so currentAssignee always exists
          const normalizedUnassignedTasks = (event.unassignedTasks || []).map(
            (task: any) => ({
              ...task,
              currentAssignee:
                task.currentAssignee || event.currentAssignee || null,
            }),
          );
          const tasksCount = event.tasksCount;
          const labelText =
            tasksCount == 1
              ? '1 of the selected task is already assigned'
              : `${tasksCount} of the selected tasks are already assigned`;
          const modalContext = {
            nodeId: modalCond.targetValue,
            assigned: event.currentAssignee?.email,
            tasksCount: event.tasksCount,
            reassignUpdate: true,
            formID: FORM_ID.TASK_REASSIGN,

            alreadyAssignedTasks: event.alreadyAssignedTasks,
            unassignedTasks: event.unassignedTasks, // ✅ use normalized tasks

            currentAssignee: event.currentAssignee,
            oldAssignee: event.oldAssignee,

            status: event.status,
            risk: event.risk,
            priority: event.priority,
            startDate: event.startDate,
            dueDate: event.dueDate,
            comment: event.comment,
            labelText: labelText,
          };
          this.activeModalContext = modalContext;

          const modal = document.getElementById('reassign_modal');
          if (modal) modal.classList.add('show');

          const closeModals = document.getElementById('bulk_assign_modal');
          if (closeModals) closeModals.classList.remove('show');

          return;
        }
      }
    }
  }

  openSuccessModal() {
    document.dispatchEvent(new CustomEvent('closeAllOverlays'));
    const fieldDeps = [
      {
        trigger: 'onClick',
        condition: [
          {
            conditions: [
              {
                type: 'modal',
                targetType: 'form',
                targetValue: FORM_ID.TASK_ASSIGN_COMPLETE,
              },
            ],
          },
        ],
      },
    ];
    const onClickDep = fieldDeps.find((dep: any) => dep.trigger === 'onClick');

    if (onClickDep && Array.isArray(onClickDep.condition)) {
      for (const condBlock of onClickDep.condition) {
        const modalCond = condBlock.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'form' && c.targetValue,
        );

        if (modalCond) {
          const modalContext = {
            nodeId: modalCond.targetValue,
            formID: FORM_ID.TASK_ASSIGN_COMPLETE,
            resetBulk: true,
          };
          this.activeModalContext = modalContext;
          const modal = document.getElementById('success_modal');
          if (modal) modal.classList.add('show');
          const closeModals =
            document.getElementById('reassign_modal') ||
            document.getElementById('bulk_assign_modal');
          if (closeModals) closeModals.classList.remove('show');

          return;
        }
      }
    }
  }

  /**
   * Closes modal if user clicks outside its content area (backdrop click)
   */
  closeBulkOnBackdropClick(event: MouseEvent) {
    const modal = document.getElementById('bulk_assign_modal');
    if (event.target === modal) {
      this.closeBulkModal();
    }
  }

  /**
   * Closes the modal and hides dropdown
   */
  closeBulkModal() {
    const modal = document.getElementById('bulk_assign_modal');
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
  }

  /**
   * Closes modal if user clicks outside its content area (backdrop click)
   */
  closeOnBackdropClicks(event: MouseEvent) {
    const modal = document.getElementById('reassign_modal');
    if (event.target === modal) {
      this.closeModals();
    }
  }

  /**
   * Closes the modal and hides dropdown
   */
  closeModals() {
    const modal = document.getElementById('reassign_modal');
    if (modal) modal.classList.remove('show');
    const modals = document.getElementById('bulk_assign_modal');
    if (modals) modals.classList.remove('show');
    this.activeModalContext = null;
  }

  /**
   * Closes modal if user clicks outside its content area (backdrop click)
   */
  closeOnBackdropSuccessClicks(event: MouseEvent) {
    const modal = document.getElementById('success_modal');
    if (event.target === modal) {
      this.closeSuccessModal();
    }
  }

  /**
   * Closes the modal and hides dropdown
   */
  closeSuccessModal() {
    const modal = document.getElementById('success_modal');
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
  }

  onBulkModalFormSaved(event: any): void {
    const STORAGE_KEY = 'tasksView';

    if (event?.success) {
      const modal = document.getElementById('bulk_assign_modal');
      if (modal) {
        modal.classList.remove('show');
      }

      this.activeModalContext = null;

      /* -------------------- Get sessionStorage -------------------- */

      const storedView = sessionStorage.getItem(STORAGE_KEY);
      const savedState = storedView ? JSON.parse(storedView) : {};

      this.selectedIds = [];
      this.selectedIds = [...this.selectedIds];

      const options: any = {
        limit: this.tasksLimit || savedState.tasksLimit || 10,
        sort: '_id',
      };

      if (savedState.taskSearch) options['query'] = savedState.taskSearch;
      if (savedState.taskStatus) options['status'] = savedState.taskStatus;
      if (savedState.taskPriority)
        options['priority'] = savedState.taskPriority;
      if (savedState.riskStatus) options['risk'] = savedState.riskStatus;
      if (savedState.domainSubdomainMappings)
        options['domainSubdomainMappings'] = savedState.domainSubdomainMappings;
      if (savedState.assignedUser)
        options['assigned'] = savedState.assignedUser;
      if (savedState.tasksLimit) options['limit'] = savedState.tasksLimit;

      setTimeout(() => {
        this.loadData.emit({
          formId: FORM_ID.TASK_LIST,
          options: options,
        });
        this.graphService.triggerGraphRefresh();
      }, 3000);
    }
  }

  // @HostListener('document:keydown.enter', ['$event'])
  // onEnter(event: any) {
  //   if (event.repeat) return;
  //   const target = event.target as HTMLElement;
  //   if (target?.tagName === 'TEXTAREA') return;
  //   // ✅ If Enter pressed inside search ignore
  //   if (target.closest('.search-field')) {
  //     return;
  //   }
  //   event.preventDefault();
  //   event.stopPropagation();

  //   if (!this.modalRendererRef?.saveForm) {
  //     return;
  //   }

  //   // this.modalRendererRef.onSave();
  //   if (this.modalRendererRef?.onSave) {
  //     this.modalRendererRef.onSave({ action: 'save' });
  //   }
  // }
}
